// SPDX-FileCopyrightText: Copyright (c) 2025 Software GmbH, Darmstadt, Germany and/or its subsidiaries and/or its affiliates
// SPDX-FileContributor: Hendrik Siemund
// SPDX-FileContributor: Jonas Schmitt
// SPDX-FileContributor: Zead Alshukairi (planen-bauen 4.0 GmbH)
// SPDX-FileContributor: Miguel Vega (planen-bauen 4.0 GmbH)
//
// SPDX-License-Identifier: Apache-2.0

import { Injectable } from '@angular/core';
import { DIDServiceDescription, DIDStatus, IDIDService } from "./did-service.interface";
import { Observable, of, throwError } from "rxjs";
import { DidGeneratorOutput2 } from '../models';
import  {HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { catchError, map } from 'rxjs/operators';

export interface WebDIDGeneratorParams {
  domain: string;
  certificateContent: string;
  keySuffix: string;
}

@Injectable({
  providedIn: 'root'
})
export class DidWebService implements IDIDService {

  private selfHosted = (location.hostname != environment.this.ourDomain);

  constructor(private http: HttpClient) { }

  /**
   * Activates a DID (not supported for web method).
   *
   * @param did the DID
   * @param params optional parameters
   * @returns an Observable that completes immediately
   */
  activateDID(did: string, params?: any): Observable<any> {
    return of(undefined);
  }

  /**
   * Deactivates a DID (not supported for web method).
   *
   * @param did the DID
   * @param params optional parameters
   * @returns an Observable that completes immediately
   */
  deactivateDID(did: string, params?: any): Observable<any> {
    return of(undefined);
  }

  /**
   * Loads a certificate file from the specified URL.
   *
   * @param url the URL to fetch the certificate from
   * @returns an Observable emitting the certificate content
   */
  loadFileFromUrl(url: string): Observable<string> {
    let endpoint = environment.server.endpoints.did_manager.fetchCert;
    let queryParams = new HttpParams().set("url", url);
    return this.http.get(endpoint, { params: queryParams, responseType: 'text' });
  }

  /**
   * Loads the SSL certificate from the specified host.
   *
   * @param host the host URL
   * @returns an Observable emitting the SSL certificate content
   */
  loadSslCertificate(host: string): Observable<string> {
    let endpoint = environment.server.endpoints.did_manager.fetchSsl;
    let queryParams = new HttpParams().set("host", host);
    return this.http.get(endpoint, { params: queryParams, responseType: 'text' });
  }

  /**
   * Generates a new DID document using the web method.
   *
   * @param params the DID generation parameters
   * @returns an Observable emitting the generated DID document
   */
  generateDID(params: WebDIDGeneratorParams, ownerId: number): Observable<{ status: string, message: string, value: DidGeneratorOutput2 | null }> {
    let endpoint = environment.server.endpoints.did_manager.didWeb;
    let queryParams = new HttpParams()
      .set("domainName", params.domain)
      .set("keySuffix", params.keySuffix)
      .set("selfHosted", this.selfHosted+"")
      .set("ownerId", ownerId+"");
    return this.http.post<any>(endpoint, params.certificateContent, { params: queryParams }).pipe(
      map((output: any) => {
        let value = {
          did: output.id,
          didDocument: output,
          privateKey: ""
        };
        return { status: "success", message: "DUD generated successfully", value: value };
      }),
      catchError((error) => {
        let msg = error.status == 409? "did.forms.create.error_did_exists" : "did.forms.create.error_msg";
        return of({ status: "error", message: msg, value: null });
      })
    );
  }

  /**
   * Gets the status of a DID (not supported for web method).
   *
   * @param did the DID
   * @returns an empty Observable
   */
  getDIDStatus(did: string): Observable<DIDStatus> {
    return of();
  }

  /**
   * Returns the description of the web DID method.
   *
   * @returns the DID service description
   */
  public getDescription(): DIDServiceDescription {
    return {
      label: "WEB",
      method: "web",
      link: "https://w3c-ccg.github.io/did-method-web/",
      supportsDIDRevocation: false,
      supportsDIDDeactivation: false,
      supportsDIDActivation: false
    }
  }

  /**
   * Resolves a DID and returns the corresponding DID document URL.
   *
   * @param did the DID to resolve
   * @returns an Observable emitting the DID document URL
   */
  resolveDID(did: string): Observable<string> {
    // For did:web, we need to resolve from the actual web domain
    // Since the backend doesn't have a resolve endpoint yet, we'll create a placeholder
    // In production, this should fetch from https://domain/.well-known/did.json

    let params = new HttpParams().set('did', did);
    return this.http.get(environment.server.endpoints.did_manager.doc, { params: params, responseType: 'text' as const })
      .pipe(
        map((downloadUrl) => downloadUrl),
        catchError((error) => {
          return throwError(() => error);
        })
      )
  }

  /**
   * Revokes a DID (not supported for web method).
   *
   * @param did the DID
   * @param params optional parameters
   * @returns an empty Observable
   */
  revokeDID(did: string, params?: any): Observable<any> {
    return of();
  }
}
